// WILLIAMS Golf Therapy - Business Plan Data
// All numbers reconciled to 200-member cap, 60K+ daily traffic, 5-location franchise roadmap
// Equity structure: 80/20 + 10% finder's fee on capital raised (not equity)

export const BRAND = {
  name: "WILLIAMS Golf Therapy",
  tagline: "The Future of Integrated Golf Performance",
  pillars: "Mind  •  Body  •  Swing  •  Recovery",
  vision:
    "A complete golf performance ecosystem — where therapy, recovery, training, and psychology unite to help golfers play better, longer, and pain-free.",
};

export const LOCATION = {
  address: "Biscayne Boulevard, Miami Shores, FL",
  proximity: "½ mile from Miami Shores Country Club",
  squareFeet: "4,900 sq ft",
  trafficCount: "60,000+",
  trafficNote: "vehicles passing daily & nightly along Biscayne Blvd",
};

export const FOUNDER = {
  name: "William Márquez",
  experience: "30+ years",
  credentials: [
    "USGA Member since 1994",
    "Licensed Physical Therapist — Sports Medicine focus",
    "PGA-affiliated coaching network",
    "Miami Shores Country Club Member",
    "Church by the Sea — Board Member",
    "Rotary Club Member",
    "Florida Chapter — Golf training for people with physical disabilities",
  ],
};

export const MARKET = {
  golfers: {
    miamiDade: 39960,
    broward: 29300,
    palmBeach: 28000, // added for full franchise footprint
    get total() {
      return this.miamiDade + this.broward + this.palmBeach;
    },
  },
  courses: { miamiDade: 40, broward: 35, palmBeach: 30, get total() { return this.miamiDade + this.broward + this.palmBeach; } },
  // Updated participation math
  perLocationCapture: 0.29, // 200 / 69,260 (MD+Broward) = 0.289%
  fullFranchiseCapture: 1.03, // 1,000 / 97,260 (MD+Broward+PB) = 1.028%
};

// ---- Membership & Revenue (corrected to 200-member cap) ----
export const MEMBERSHIP = {
  cap: 200,
  priceMonthly: 320,
  get monthlyMembershipRevenue() {
    return this.cap * this.priceMonthly; // $64,000
  },
};

export const MONTHLY_REVENUE = {
  membership: 64000,           // 200 × $320
  performancePackages: 5000,   // High-end personal training, custom packages
  insuranceReimbursement: 3000,// PT insurance billing
  ancillary: 3000,             // Pro-shop, nutrition products, equipment fitting, guest fees
  get total() {
    return this.membership + this.performancePackages + this.insuranceReimbursement + this.ancillary;
  }, // $75,000
};

export const MONTHLY_OPEX = {
  baseRentNNN: 20900,
  payroll: 15000,
  marketing: 5000,
  utilitiesInternet: 1200,
  suppliesLaundry: 800,
  professionalServices: 500,
  insurance: 400,
  loanPayment: 5400, // $300K @ 8% / 5 years
  get total() {
    return (
      this.baseRentNNN +
      this.payroll +
      this.marketing +
      this.utilitiesInternet +
      this.suppliesLaundry +
      this.professionalServices +
      this.insurance +
      this.loanPayment
    );
  }, // $49,200
};

export const STARTUP_CAPITAL = {
  items: [
    { label: "Lease Deposits (2 months)", amount: 47366 },
    { label: "Build-Out & Permitting", amount: 35000 },
    { label: "Equipment & Technology", amount: 55000 },
    { label: "Branding & Signage", amount: 10000 },
    { label: "Marketing & Launch", amount: 15000 },
    { label: "Payroll Buffer (3 months)", amount: 30000 },
    { label: "Insurance, Legal, Licensing", amount: 8000 },
    { label: "Working Capital Reserve (60–90 days)", amount: 20000 },
    { label: "Contingency / Investment Buffer", amount: 79634 },
  ],
  total: 300000,
};

// ---- 5-Year Projections (corrected for 200-member cap, $15K/mo payroll, realistic ramp) ----
// Payroll bump of $5K/mo = $60K/yr per site. OpEx scales accordingly with each franchise added.
export const PROJECTIONS = [
  { year: "Year 1", members: 110, occupancy: "55%", revenue: 420000, opex: 570000, netProfit: -150000, note: "Launch & ramp-up" },
  { year: "Year 2", members: 165, occupancy: "82%", revenue: 685000, opex: 590000, netProfit: 95000, note: "Break-even crossed mid-year" },
  { year: "Year 3", members: 200, occupancy: "100% + waitlist", revenue: 900000, opex: 605000, netProfit: 295000, note: "Full capacity • Franchise documentation begins" },
  { year: "Year 4", members: 200, occupancy: "100% + 2 franchise sites", revenue: 1850000, opex: 1360000, netProfit: 490000, note: "Locations 2–3 launch (Coral Gables, Ft. Lauderdale)" },
  { year: "Year 5", members: 200, occupancy: "100% + 4 franchise sites", revenue: 3650000, opex: 2540000, netProfit: 1110000, note: "Locations 4–5 launch (Boca Raton, Palm Beach)" },
];

// Reconciled with the original $228K Year-3 figure (after debt service + tax provision)
// New: Y3 net $295K × ~64% retention (after tax) ≈ $190K
export const YEAR3_NET_AFTER_DEBT_TAX = 190000;

// ---- Equity & Investment Structure ----
export const EQUITY = {
  structure: [
    { holder: "William Márquez (Founder & Operator)", percent: 80 },
    { holder: "Investor Group", percent: 20 },
  ],
  finderFee: {
    label: "Finder's Fee",
    description:
      "One-time commission paid to capital-raise facilitators — 10% of capital raised ($30,000 on a $300K raise). Not equity.",
    amountOnSeed: 30000,
  },
  totalCheck: 100,
};

export const INVESTMENT_OPTIONS = [
  {
    title: "Convertible Loan",
    terms: "5 years @ 8% interest",
    description:
      "Fixed interest income with optional conversion to equity at franchise sale event. Lowest risk path.",
    ideal: "Income-oriented investors",
  },
  {
    title: "Equity Participation",
    terms: "20% ownership stake",
    description:
      "Direct equity in the prototype LLC. Exit payout at franchise sale (Year 5–7). Proportional share of distributions.",
    ideal: "Growth-oriented investors",
  },
  {
    title: "Strategic Partnership",
    terms: "Blended debt + equity",
    description:
      "Hybrid structure with expansion funding commitment and active role in franchise development & site selection.",
    ideal: "Operating partners / multi-unit operators",
  },
];

export const EXIT_SCENARIOS = [
  { sale: 10000000, founder: 8000000, investors: 2000000, multiple: "6.7×" },
  { sale: 12000000, founder: 9600000, investors: 2400000, multiple: "8.0×" },
  { sale: 15000000, founder: 12000000, investors: 3000000, multiple: "10.0×" },
];

// ---- Services & Technology ----
export const SERVICES = [
  {
    title: "Sports Medicine",
    icon: "stethoscope",
    image: "/brand/svc-sports-med.jpg",
    bullets: [
      "Orthopedic & joint assessment",
      "Manual therapy & dry needling",
      "Shockwave & laser therapy",
      "Medical-grade treatment protocols",
    ],
  },
  {
    title: "Rehabilitation",
    icon: "activity",
    image: "/brand/svc-rehab-massage.jpg",
    bullets: [
      "Post-surgical golf-return programs",
      "Injury recovery (rotator cuff, hip, lumbar, elbow)",
      "Movement re-patterning",
      "Insurance-billable PT services",
    ],
  },
  {
    title: "Physical Fitness",
    icon: "dumbbell",
    image: "/brand/svc-fitness-mobility.jpg",
    bullets: [
      "Certified strength & conditioning trainers",
      "Mobility & flexibility programs",
      "Force-plate balance training",
      "Recovery suite (cold plunge, compression, sauna)",
    ],
  },
  {
    title: "Nutrition",
    icon: "apple",
    image: "/brand/svc-nutrition.jpg",
    bullets: [
      "Performance nutrition planning",
      "Hydration & on-course fuelling protocols",
      "Body composition tracking (InBody)",
      "Supplement & recovery products",
    ],
  },
  {
    title: "Sports Psychology",
    icon: "brain",
    image: "/brand/svc-sports-psych.jpg",
    bullets: [
      "Mental performance coaching",
      "Focus, commitment, process & resilience training",
      "Pre-shot routine & competitive mindset",
      "Visualization, breathwork & confidence protocols",
    ],
  },
  {
    title: "Coaching",
    icon: "trophy",
    image: "/brand/svc-coaching-band.jpg",
    bullets: [
      "PGA-affiliated instruction",
      "Biomechanics-driven swing analysis",
      "Short-game & on-course strategy",
      "Junior development & senior longevity programs",
    ],
  },
  {
    title: "Golf Technology",
    icon: "radar",
    image: "/brand/tech-trackman.jpg",
    bullets: [
      "TrackMan 4 launch monitor",
      "Full Swing / Foresight GCQuad simulators",
      "K-Vest / HackMotion 3D biomechanics",
      "BodiTrak pressure mapping & force plates",
    ],
  },
];

// ---- Brand Visuals ----
export const HERO_BG = "/brand/signage-night.jpg";

export const FOUNDER_IMAGES = {
  primary: "/brand/founder-putting-green.jpg",
  secondary: "/brand/founder-mountain.jpg",
};

export const FACILITY_GALLERY = [
  {
    image: "/brand/renderings-split.jpg",
    eyebrow: "The Vision",
    title: "Day & Night Concept",
    text: "Architectural renderings of the Biscayne Blvd flagship — Mind · Body · Swing · Recovery integrated into the building itself.",
  },
  {
    image: "/brand/storefront-day.jpg",
    eyebrow: "The Build",
    title: "Storefront Facade",
    text: "Branded entryway under the WILLIAMS Golf Therapy emblem — a complete golf performance ecosystem, signaled at the door.",
  },
  {
    image: "/brand/signage-night.jpg",
    eyebrow: "The Brand Presence",
    title: "Illuminated Roadside Signage",
    text: "High-visibility signage activates the 60,000+ daily Biscayne Blvd vehicle traffic — day and night.",
  },
  {
    image: "/brand/location-biscayne.jpg",
    eyebrow: "The Site",
    title: "Biscayne Boulevard, Miami Shores",
    text: "Today's site — Biscayne Plaza, ½ mile from Miami Shores Country Club. The flagship begins here.",
  },
];

// ---- Franchise Roadmap (5 locations, corrected) ----
export const FRANCHISE_ROADMAP = [
  { phase: "Year 1", milestone: "Miami Shores Flagship", detail: "Prototype launch, brand validation, KPI baseline" },
  { phase: "Year 2", milestone: "Proof of Concept", detail: "Member metrics validated, franchise legal docs (FDD) prepared" },
  { phase: "Year 3", milestone: "Franchise Ready", detail: "Operations manual finalized, full capacity at flagship" },
  { phase: "Year 4", milestone: "Locations 2 & 3", detail: "Coral Gables + Fort Lauderdale corridors" },
  { phase: "Year 5", milestone: "Locations 4 & 5", detail: "Boca Raton + Palm Beach — corridor complete" },
  { phase: "Years 6–7", milestone: "Exit / Scale", detail: "Strategic sale, regional master franchise, or PE rollup" },
];

// ---- Differentiators ----
export const DIFFERENTIATORS = [
  {
    title: "The Only Integrated Model in South Florida",
    text:
      "Competitors offer therapy OR coaching OR fitness OR tech — never under one roof, one membership, one diagnostic record.",
  },
  {
    title: "Insurance-Billable Revenue Layer",
    text:
      "Licensed PT services generate insurance reimbursement on top of memberships — a revenue stream no traditional golf academy can access.",
  },
  {
    title: "Captive, High-Income Catchment",
    text:
      "60,000+ vehicles daily on Biscayne Blvd; ½ mile from Miami Shores CC; minutes from Bay Point, Belle Meade, and Indian Creek.",
  },
  {
    title: "Franchise-First Engineering",
    text:
      "Every system — from intake to programming to billing — is built to be replicated. The flagship is the playbook.",
  },
];

// ---- Why Now ----
export const WHY_NOW = [
  "Post-pandemic golf participation is at a 20-year high (NGF 2025).",
  "Wellness + longevity is the fastest-growing consumer category in South Florida.",
  "Insurance reimbursement for sports PT has expanded under 2024–25 CMS rulings.",
  "Golf-tech (TrackMan, Foresight, biomechanics) is now mainstream — and consumer-expected.",
  "No competitor in the Palm Beach → Miami Shores corridor offers the integrated model.",
];

// ---- KPIs (top of page hero stats) ----
export const HERO_KPIS = [
  { label: "Seed Capital", value: "$300K", sub: "Convertible / Equity / Hybrid" },
  { label: "Year 3 Revenue", value: "$900K", sub: "200 members at capacity" },
  { label: "Year 5 Revenue", value: "$3.65M", sub: "Across 5 locations" },
  { label: "Target Return", value: "11–13×", sub: "5–7 year horizon" },
];

// ---- Investor Call CTA & Contact ----
export const CONTACT = {
  email: "williamsgolftherapy@gmail.com",
  phone: "786-222-0533",
  phoneTel: "+17862220533",
  address: "Biscayne Boulevard, Miami Shores, FL",
  founder: "William Márquez, Founder",
};

export const INVESTOR_CTA = {
  href: `mailto:${"williamsgolftherapy@gmail.com"}?subject=${encodeURIComponent("Investor Inquiry — WILLIAMS Golf Therapy")}&body=${encodeURIComponent("Hello William,\n\nI'd like to learn more about the WILLIAMS Golf Therapy franchise investment opportunity. Please share next steps for a 30-minute call.\n\nBest regards,\n")}`,
  label: "Schedule an Investor Call",
  sub: "30-minute private session with William Márquez",
};

// ---- Competitive comparison ----
export const COMPARISON = {
  competitors: ["WILLIAMS Golf Therapy", "TPI Institute", "GolfTec", "Traditional PT"],
  rows: [
    { feature: "Sports Medicine (in-house)",   values: ["full",    "cert-only", "none",    "full"] },
    { feature: "Licensed Rehab / PT",          values: ["full",    "referral",  "none",    "full"] },
    { feature: "Strength & Conditioning",      values: ["full",    "partner",   "none",    "limited"] },
    { feature: "Performance Nutrition",        values: ["full",    "none",      "none",    "none"] },
    { feature: "PGA-Affiliated Coaching",      values: ["full",    "referral",  "full",    "none"] },
    { feature: "Golf Tech (TrackMan, sims)",   values: ["full",    "none",      "partial", "none"] },
    { feature: "Insurance Reimbursement",      values: ["full",    "none",      "none",    "full"] },
    { feature: "Single Unified Membership",    values: ["full",    "n/a",       "per lesson", "per visit"] },
    { feature: "One Integrated Member Record", values: ["full",    "none",      "none",    "limited"] },
    { feature: "Mind / Sport Psychology",      values: ["full",    "partner",   "none",    "none"] },
  ],
};

// ---- UI Labels (section titles, buttons, etc.) ----
export const UI = {
  nav: {
    investorBusinessPlan: "Investor Business Plan",
    downloadPdf: "Download PDF",
    items: { vision: "Vision", market: "Market", services: "Services", financials: "Financials", investment: "Investment", roadmap: "Roadmap", contact: "Contact" },
  },
  hero: { eyebrow: "Franchise Prototype · Investment Memorandum" },
  founder: {
    title: "The Founder",
    kicker: "30+ years of clinical sports medicine and golf performance — a rare blend of medical practitioner, USGA-certified golfer, and community leader.",
    whyTitle: "Why this Founder, why now",
    whyText: "The integrated model only works when one operator can speak the language of orthopedics, biomechanics, coaching, and business at the same table. William has spent three decades doing exactly that — earning the trust of patients, country-club members, insurers, and the PGA network alike.",
    photoLabel: "Founder & Operator",
  },
  market: {
    eyebrow: "Market Opportunity",
    title: "A defensible foothold in South Florida's golf corridor",
    kicker: "The Miami Shores → Palm Beach corridor concentrates the highest density of active golfers, country clubs, and discretionary-income households in the state.",
    miamiDadeLabel: "Miami-Dade golfers",
    browardLabel: "Broward golfers",
    palmBeachLabel: "Palm Beach golfers",
    coursesNote: (n) => `${n}+ courses`,
    totalLabel: "Total Addressable Golfers",
    totalNote: (n) => `Across ${n}+ golf courses — the densest concentration of paying golfers in the southeast US.`,
    shareLabel: "Required Market Share",
    perSite: "per location (200 members)",
    fullRollout: "full 5-franchise rollout",
    shareCaption: "Capturing roughly 1 in every 100 area golfers fills the entire franchise system — a conservative, achievable target.",
    locationTitle: "Location",
    squareFeetLabel: "Square Feet",
    countryClubLabel: "Country Club",
    trafficLabel: "Daily Traffic",
    visibilityLabel: "Visibility",
    dayNight: "Day & Night",
    vehiclesUnit: "vehicles",
  },
  diff: { eyebrow: "What sets us apart", title: "The only integrated golf performance ecosystem in South Florida" },
  comparison: {
    eyebrow: "Competitive Landscape",
    title: "Seven categories. One operator delivers all seven.",
    kicker: "Every comparable operator in the South Florida market specializes in just one or two of the pillars below. None offer the integrated membership model.",
    capability: "Capability",
    footnote: "Comparison reflects publicly available service offerings and standard operating models as of 2026. TPI = Titleist Performance Institute.",
    cellLabels: { full: "Full", partial: "Partial", referral: "Referral", limited: "Limited", partner: "Partner", "cert-only": "Cert only", none: "None", "n/a": "N/A", "per lesson": "Per lesson", "per visit": "Per visit" },
  },
  facility: {
    eyebrow: "Brand Realization",
    title: "From rendering to reality — the flagship takes shape.",
    kicker: "Architectural concept, branded build-out, and roadside presence — engineered to convert 60,000+ daily Biscayne Blvd impressions into members.",
  },
  services: {
    eyebrow: "Service Lines",
    title: "Seven pillars. One membership. One record.",
    kicker: "Members move freely across all service lines under a single program — eliminating the fragmentation that defines every competitor in the region.",
  },
  financials: {
    eyebrow: "Financial Snapshot",
    title: "Reconciled numbers. 200-member cap. Realistic ramp.",
    kicker: "All projections below reflect the corrected 200-member membership cap at $320/month, plus performance packages, insurance reimbursement, and ancillary revenue.",
    monthlyRevenue: "Monthly Revenue at Capacity",
    revenue: { membership: "Memberships (200 × $320)", performance: "Performance packages & add-ons", insurance: "Insurance reimbursement (PT)", ancillary: "Ancillary (pro-shop, nutrition, fittings)", total: "Total Monthly Revenue" },
    monthlyOpex: "Monthly Operating Expenses",
    opex: { rent: "Base rent + NNN", payroll: "Payroll (PTs, front desk, assistants)", marketing: "Marketing (digital, local, referral)", utilities: "Utilities & internet", supplies: "Supplies & laundry", professional: "Professional services", insurance: "Insurance (GL + malpractice)", loan: "Loan payment ($300K @ 8% / 5y)", total: "Total Monthly OpEx" },
    netProfitMonthly: "Net Monthly Profit (at capacity)",
    operatingMargin: "Operating Margin",
    year3Net: "Year-3 Net (after debt + tax)",
    startupAlloc: "Start-Up Capital Allocation",
    totalCapital: "Total Capital Required",
    useOfFunds: "Use of Funds (Visual)",
    projectionTitle: "5-Year Projection (Flagship + Franchise Rollout)",
    tableHead: { year: "Year", members: "Members / Sites", revenue: "Revenue", opex: "OpEx", net: "Net Profit", milestone: "Milestone" },
    membersUnit: "mbrs",
    tableFootnote: "Year-1 reflects a launch-and-ramp period; break-even crosses in Q2 of Year 2. Year-3 net reflects flagship-only after debt service and tax provision.",
    chartEyebrow: "Growth Trajectory",
    chartTitle: "5-Year Revenue & Net Profit",
    chartLegendRevenue: "Revenue",
    chartLegendProfit: "Net Profit",
    chartFootnote: "Year 1 reflects flagship launch & ramp. Break-even crosses mid-Year 2. Years 4–5 reflect cumulative revenue across active franchise sites.",
  },
  investment: {
    eyebrow: "Investment Structure",
    title: "Three paths in. One franchise out.",
    kicker: "Flexible structures for capital partners. Equity totals 100%. The Finder's Fee is a one-time commission on capital raised — not equity.",
    capTable: "Cap Table (Post-Seed)",
    totalEquity: "Total Equity",
    finderFeeAmount: "10% of capital raised",
    onSeed: "On the $300K seed raise",
    feeAmount: "one-time fee",
    idealFor: "Ideal for:",
    exitTitle: "Exit Scenarios (Years 5–7)",
    exitSub: "Strategic sale or regional master franchise — projected returns by sale price.",
    exitTable: { salePrice: "Sale Price", founder: "Founder (80%)", investors: "Investors (20%)", multiple: "Investor Multiple on $300K" },
  },
  roadmap: {
    eyebrow: "Franchise Roadmap",
    title: "5 locations. Miami Shores → Palm Beach. 5–7 years.",
    kicker: "The flagship validates the model. Years 4–5 deploy 4 additional locations northward along Florida's wealthiest golf corridor.",
  },
  whyNow: { eyebrow: "Why Now", title: "Five forces converging — only briefly." },
  contact: {
    eyebrow: "Get In Touch",
    title: "Let's discuss the opportunity.",
    kicker: "Reach out directly to William Márquez to schedule a private investor conversation or facility tour at the Miami Shores flagship.",
    emailLabel: "Email",
    phoneLabel: "Phone",
    visitLabel: "Visit",
    emailHint: "Click to compose & copy address",
    phoneHint: "Click to call & copy number",
    toastEmail: "Email address copied to clipboard",
    toastPhone: "Phone number copied to clipboard",
    quote: "Let's build the future of integrated golf performance — together.",
  },
  footer: { pillars: "Mind · Body · Swing · Recovery", inquiries: "Investor Inquiries", confidential: "Confidential investor memorandum." },
  common: { tomembers: "mbrs", break_even: "Launch & ramp-up" },
};
